import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { focusManager, useQuery } from '@tanstack/react-query';
import { compareVersions } from 'compare-versions';
import Constants from 'expo-constants';
import * as Updates from 'expo-updates';
import { useEffect, useRef, useState } from 'react';
import { AppState, AppStateStatus } from 'react-native';

import {
  trackAppConfig,
  trackUpdateAction,
  trackUpdateCompleted,
  trackUpdateFailed,
  UPDATE_ACTIONS,
} from '@/analytics/update';
import { ONE_DAY } from '@/constants/time';
import { getAppConfigV2QueryOptions, type AppConfig } from '@/services/getAppConfigV2';
import { Sentry } from '@/util/sentry';

const FOREGROUND_CHECK_COOLDOWN_MS = ONE_DAY;

type UpdateType = 'ota' | 'store' | null;

type UseUpdateManagerResult = {
  isLoading: boolean;
  isChecking: boolean;
  isDownloading: boolean;
  isUpdatePending: boolean;
  showRequiredUpdate: boolean;
  updateType: UpdateType;
  required: boolean;
  message?: string;

  shouldCheckForUpdates: boolean;
  endpointError: boolean;

  currentVersion: string;
  appConfig?: AppConfig;
};

/**
 * Central manager for OTA and store update UX driven by `/v2/app-config`.
 *
 * OTA behavior matrix (`otaUpdate.mode` + `otaUpdate.required`):
 *
 * | mode   | required | JS check | On update found                        | Banner/badge |
 * |--------|----------|----------|----------------------------------------|--------------|
 * | auto   | false    | No       | Expo native — silent, next cold start  | Hidden       |
 * | auto   | true     | Yes*     | RequiredUpdateModal (blocking)         | Hidden       |
 * | prompt | false    | Yes      | Silent download → banner + badge       | Shown        |
 * | prompt | true     | Yes      | RequiredUpdateModal (blocking)         | Moot         |
 *
 * *`required: true` coerces mode to `prompt` internally.
 *
 * Store: prompts when `latestVersion` from server is greater than current binary version.
 * Priority: required store > required OTA > optional store.
 */
export const useUpdateManager = (): UseUpdateManagerResult => {
  const currentVersion = Constants.expoConfig?.version || '0.0.0';

  const {
    data: appConfig,
    isLoading: configLoading,
    error: configError,
  } = useQuery({ ...getAppConfigV2QueryOptions(), enabled: !isWeb });

  useEffect(() => {
    if (!configLoading && !isWeb) {
      if (appConfig) {
        trackAppConfig({
          status: 'loaded',
          hasServerConfig: true,
          config: {
            otaUpdate: appConfig.otaUpdate,
            storeUpdate: appConfig.storeUpdate,
          },
        });
      } else if (configError) {
        trackAppConfig({
          status: 'failed',
          error: configError.message || 'Unknown error',
          fallbackConfig: {
            otaUpdate: { mode: 'auto', required: false },
            storeUpdate: { mode: 'auto', required: false },
          },
        });
      }
    }
  }, [appConfig, configLoading, configError]);

  const { isChecking, isDownloading, isUpdatePending } = Updates.useUpdates();

  const endpointError = !!configError;

  const [hasOtaAvailable, setHasOtaAvailable] = useState(false);

  const lastForegroundCheckRef = useRef<number>(0);

  const [otaCheckResolved, setOtaCheckResolved] = useState(() => {
    return (appConfig?.otaUpdate?.mode ?? 'auto') !== 'prompt';
  });

  type PolicyMode = 'auto' | 'prompt';

  const deriveMode = (policy?: { mode?: PolicyMode }): PolicyMode => {
    return policy?.mode ?? 'auto';
  };

  const isCurrentVersionOutdated = (current: string, latest: string): boolean => {
    if (!current || !latest) {
      return false;
    }

    try {
      return compareVersions(latest.trim(), current.trim()) === 1;
    } catch {
      return false;
    }
  };

  const otaPolicy = {
    mode: appConfig?.otaUpdate?.required ? 'prompt' : deriveMode(appConfig?.otaUpdate),
    required: appConfig?.otaUpdate?.required ?? false,
    message: appConfig?.otaUpdate?.message,
  } as const;

  const storePolicy = {
    mode: deriveMode(appConfig?.storeUpdate),
    required: appConfig?.storeUpdate?.required ?? false,
    message: appConfig?.storeUpdate?.message,
    latestVersion: appConfig?.storeUpdate?.latestVersion,
  } as const;

  let updateType: UpdateType = null;

  let required = false;

  const needsStoreUpdate = (): boolean => {
    if (storePolicy.mode !== 'prompt') {
      return false;
    }

    if (!storePolicy.latestVersion) {
      return false;
    }

    const otaPromptEnabled = otaPolicy.mode === 'prompt';

    if (!storePolicy.required && otaPromptEnabled && !otaCheckResolved) {
      return false;
    }

    return isCurrentVersionOutdated(currentVersion, storePolicy.latestVersion);
  };

  if (needsStoreUpdate() && storePolicy.required) {
    updateType = 'store';

    required = true;
  } else if (otaPolicy.mode !== 'auto' && (hasOtaAvailable || isUpdatePending)) {
    updateType = 'ota';

    required = otaPolicy.required;
  } else if (!updateType && needsStoreUpdate()) {
    updateType = 'store';

    required = storePolicy.required;
  }

  const shouldCheckForUpdates = otaPolicy.mode !== 'auto' || storePolicy.mode !== 'auto';

  const showRequiredUpdate =
    !isWeb && !!appConfig && !configLoading && required && updateType !== null;

  useEffect(() => {
    if (isWeb) {
      return;
    }

    async function foregroundCheck() {
      try {
        const result = await Updates.checkForUpdateAsync();

        if (result.isAvailable && !otaPolicy.required) {
          trackUpdateAction({
            updateType: 'background',
            action: UPDATE_ACTIONS.BACKGROUND_DOWNLOAD,
            currentVersion,
            isDefault: endpointError,
          });

          try {
            await Updates.fetchUpdateAsync();

            setHasOtaAvailable(true);

            trackUpdateCompleted('background', currentVersion, false, endpointError);
          } catch (fetchError) {
            Sentry.captureException(fetchError, {
              tags: { component: 'useUpdateManager', source: 'foreground-fetch' },
              extra: {
                currentVersion,
                errorMessage: fetchError instanceof Error ? fetchError.message : String(fetchError),
              },
            });

            trackUpdateFailed(
              'background',
              currentVersion,
              fetchError instanceof Error ? fetchError.message : 'Unknown error',
              false,
              endpointError
            );
          }
        } else if (result.isAvailable && otaPolicy.required) {
          setHasOtaAvailable(true);
        }
      } catch (checkError) {
        Sentry.captureException(checkError, {
          tags: { component: 'useUpdateManager', source: 'foreground-check' },
          extra: {
            currentVersion,
            errorMessage: checkError instanceof Error ? checkError.message : String(checkError),
          },
        });
      }
    }

    function onAppStateChange(status: AppStateStatus) {
      focusManager.setFocused(status === 'active');

      if (
        status !== 'active' ||
        otaPolicy.mode !== 'prompt' ||
        isUpdatePending ||
        !Updates.isEnabled
      ) {
        return;
      }

      const now = Date.now();

      if (
        !otaPolicy.required &&
        now - lastForegroundCheckRef.current < FOREGROUND_CHECK_COOLDOWN_MS
      ) {
        return;
      }

      lastForegroundCheckRef.current = now;

      void foregroundCheck();
    }

    const subscription = AppState.addEventListener('change', onAppStateChange);

    return () => subscription.remove();
  }, [isUpdatePending, otaPolicy.mode, otaPolicy.required, currentVersion, endpointError]);

  useEffect(() => {
    if (isWeb) {
      return;
    }

    if (otaPolicy.mode !== 'prompt') {
      return;
    }

    if (otaCheckResolved) {
      return;
    }

    const timer = setTimeout(() => {
      setOtaCheckResolved(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, [isWeb, otaPolicy.mode, otaCheckResolved]);

  useEffect(() => {
    let isMounted = true;

    async function initialCheck() {
      if (isWeb) {
        return;
      }

      if (otaPolicy.mode !== 'prompt') {
        return;
      }

      if (!Updates.isEnabled) {
        return;
      }

      try {
        Sentry.addBreadcrumb({
          category: 'ota-update',
          message: 'Cold-start OTA check started',
          level: 'info',
          data: { currentVersion, required: otaPolicy.required },
        });

        const result = await Updates.checkForUpdateAsync();

        if (!isMounted) {
          return;
        }

        setHasOtaAvailable(!!result.isAvailable);

        setOtaCheckResolved(true);

        Sentry.addBreadcrumb({
          category: 'ota-update',
          message: 'Cold-start OTA check resolved',
          level: 'info',
          data: { isAvailable: result.isAvailable, required: otaPolicy.required },
        });

        if (result.isAvailable && !otaPolicy.required) {
          trackUpdateAction({
            updateType: 'background',
            action: UPDATE_ACTIONS.BACKGROUND_DOWNLOAD,
            currentVersion,
            isDefault: endpointError,
          });

          try {
            await Updates.fetchUpdateAsync();

            if (!isMounted) {
              return;
            }

            trackUpdateCompleted('background', currentVersion, false, endpointError);
          } catch (fetchError) {
            if (!isMounted) {
              return;
            }

            Sentry.captureException(fetchError, {
              tags: { component: 'useUpdateManager', source: 'background-fetch' },
              extra: {
                currentVersion,
                errorMessage: fetchError instanceof Error ? fetchError.message : String(fetchError),
              },
            });

            trackUpdateFailed(
              'background',
              currentVersion,
              fetchError instanceof Error ? fetchError.message : 'Unknown error',
              false,
              endpointError
            );
          }
        }
      } catch (checkError) {
        if (!isMounted) {
          return;
        }

        Sentry.captureException(checkError, {
          tags: { component: 'useUpdateManager', source: 'cold-start-check' },
          extra: {
            currentVersion,
            errorMessage: checkError instanceof Error ? checkError.message : String(checkError),
          },
        });

        setHasOtaAvailable(false);

        setOtaCheckResolved(true);
      }
    }

    initialCheck();

    return () => {
      isMounted = false;
    };
  }, [isWeb, otaPolicy.mode, otaPolicy.required, currentVersion, endpointError]);

  return {
    isLoading: configLoading,
    isChecking,
    isDownloading,
    isUpdatePending,
    showRequiredUpdate,
    updateType,
    required,
    message: updateType === 'ota' ? otaPolicy.message : storePolicy.message,

    shouldCheckForUpdates,
    endpointError,

    currentVersion,
    appConfig,
  };
};
